import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";

import { getParcel } from "@/api/parcels";
import type { ParcelViewDto } from "@/types/domain";

interface ParcelPreviewModalProps {
  parcelNumber: number;
  open: boolean;
  onClose: () => void;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  return JSON.stringify(v);
}

interface KvSectionProps {
  title: string;
  data: Record<string, unknown> | null | undefined;
}

function KvSection({ title, data }: KvSectionProps) {
  const entries = Object.entries(data ?? {}).filter(
    ([, v]) => v !== null && v !== undefined,
  );
  if (entries.length === 0) {
    return null;
  }
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Text size={300} weight="semibold">
        {title}
      </Text>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(140px, max-content) 1fr",
          rowGap: 4,
          columnGap: 12,
          fontSize: 13,
        }}
      >
        {entries.map(([k, v]) => (
          <KvRow key={k} label={k} value={v} />
        ))}
      </div>
    </section>
  );
}

function KvRow({ label, value }: { label: string; value: unknown }) {
  const isComplex = typeof value === "object" && value !== null;
  return (
    <>
      <span
        style={{
          color: tokens.colorNeutralForeground2,
          fontFamily: "ui-monospace, monospace",
        }}
      >
        {label}
      </span>
      {isComplex ? (
        <pre
          style={{
            margin: 0,
            fontFamily: "ui-monospace, monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : (
        <span style={{ wordBreak: "break-word" }}>{formatValue(value)}</span>
      )}
    </>
  );
}

function pickHeader(parcel: ParcelViewDto): Record<string, unknown> {
  const out: Record<string, unknown> = {
    parcelNumber: parcel.parcelNumber,
    parcelStatus: parcel.parcelStatus,
    parcelStatusDescription: parcel.parcelStatusDescription,
    tradeNumber: parcel.tradeNumber,
    orderNumber: parcel.orderNumber,
    itemNumber: parcel.itemNumber,
    nominatedQuantity: parcel.nominatedQuantity,
    deliveryFromDate: parcel.deliveryFromDate,
    deliveryEndDate: parcel.deliveryEndDate,
  };
  return out;
}

export function ParcelPreviewModal({
  parcelNumber,
  open,
  onClose,
}: ParcelPreviewModalProps) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["parcel", parcelNumber],
    queryFn: () => getParcel(parcelNumber),
    enabled: open,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) onClose();
      }}
    >
      <DialogSurface style={{ maxWidth: 720 }}>
        <DialogBody>
          <DialogTitle>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              Parcel {parcelNumber}
              {data?.parcelStatusDescription ? (
                <Badge appearance="tint" color="informative">
                  {data.parcelStatusDescription}
                </Badge>
              ) : null}
            </span>
          </DialogTitle>
          <DialogContent>
            {isLoading ? (
              <Spinner label="Loading Symphony state…" />
            ) : isError ? (
              <MessageBar intent="error">
                <MessageBarBody>
                  {error instanceof Error
                    ? error.message
                    : "Failed to load parcel"}
                </MessageBarBody>
              </MessageBar>
            ) : data ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <KvSection title="Header" data={pickHeader(data)} />
                <Divider />
                <KvSection
                  title="Bill of Lading"
                  data={data.billOfLadingInfo}
                />
                <Divider />
                <KvSection
                  title="Loading / Discharge"
                  data={data.loadingDischargeInfo}
                />
                {Array.isArray(data.actuals) && data.actuals.length > 0 ? (
                  <>
                    <Divider />
                    <section
                      style={{ display: "flex", flexDirection: "column", gap: 6 }}
                    >
                      <Text size={300} weight="semibold">
                        Actuals
                      </Text>
                      {data.actuals.map((row, idx) => (
                        <div
                          key={idx}
                          style={{
                            border: `1px solid ${tokens.colorNeutralStroke3}`,
                            borderRadius: 4,
                            padding: 8,
                          }}
                        >
                          <KvSection title={`#${idx + 1}`} data={row} />
                        </div>
                      ))}
                    </section>
                  </>
                ) : null}
                {data.allocationItem ? (
                  <>
                    <Divider />
                    <KvSection
                      title="Allocation"
                      data={data.allocationItem}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
